import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { StateController } from '../state';
import { a11yStyles } from '../styles/a11y';
import { close } from '../svg';

@customElement('umb-market-page-title')
export class UmbMarketPageTitle extends LitElement {
  @property({ type: String })
  pageTitle?: string;

  @property({ type: String })
  pageDescription?: string;

  @property({ type: Boolean })
  srOnly = false;

  @state()
  private _title?: string;

  @state()
  private _description?: string;

  @state()
  private _bannerText?: string;

  @state()
  private _bannerIcon?: string;

  private _showClear = false;

  connectedCallback() {
    super.connectedCallback();

    if (StateController.isCategoryView) {
      const category = StateController.current.categories?.find(x => x.active);

      let description = category?.description;
      const name = category?.name;

      if (!description?.startsWith('<p>')) {
        description = `<p>${description}</p>`;
      }

      this._bannerText = `<div><h2>${name}</h2> ${description}</div>`;
      this._bannerIcon = category?.icon;

      return;
    }

    this._title = this.pageTitle ?? StateController.current.pageTitle;
    this._description =
      this.pageDescription ?? StateController.current.pageDescription;

    this._showClear = StateController.current.showClear;

    if (!this._title || !this._description) {
      return;
    }

    this._bannerText = `<div><h2>${this._title}</h2> ${this._description}</div>`;
  }

  // eslint-disable-next-line class-methods-use-this
  #clear() {
    StateController.current.queryKey =
      StateController.current.query =
      StateController.current.pageTitle =
        '';

    // when tag/search is cleared, route back to the parent node, and display all items
    window.history.pushState(null, '', `/all`);

    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  #renderPageTitle() {
    if (this._showClear) {
      return html`<div>
        <h1 class=${this.srOnly ? 'sr-only' : ''}>
          ${unsafeHTML(this._title)}
        </h1>
        <umb-market-button id="clearButton" @click=${this.#clear}>
          ${close}
        </umb-market-button>
      </div>`;
    }

    return html`<h1 class=${this.srOnly ? 'sr-only' : ''}>
      ${unsafeHTML(this._title)}
    </h1>`;
  }

  render() {
    return html` ${when(this._title && !this._bannerText, () =>
      this.#renderPageTitle()
    )}
    ${when(
      this._bannerText && !StateController.current.isBackoffice,
      () =>
        html`<umb-market-banner
          .text=${this._bannerText!}
          .icon=${this._bannerIcon}
        ></umb-market-banner>`
    )}`;
  }

  static styles = [
    a11yStyles,
    css`
      h1 {
        color: var(--uui-color-default);
        font-size: 1.5em;
        -webkit-font-smoothing: antialiased;
        line-height: 1.3;
        font-weight: 400;
        text-align: center;
        margin: calc(-1 * var(--s-spacer)) auto 0;
        max-width: 900px;
      }

      h1:last-child,
      p {
        margin-bottom: var(--xl-spacer);
      }

      #clearButton {
        --button-border-radius: 100px;
        --button-width: var(--btn-round-size);
        --button-height: var(--btn-round-size);
        --fill: var(--uui-color-default);
        margin-left: var(--xs-spacer);
      }

      div {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: var(--xl-spacer);
      }

      div h1 {
        margin-left: 0;
        margin-right: 0;
      }

      @media (min-width: 768px) {
        h1 {
          margin-top: 0;
        }
      }
    `,
  ];
}
